import { useRef, useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import '@assets/css/Admin/components/calendar.scss';
import DeleteModal from '@components/DeleteModal';
import { format, parseISO } from 'date-fns';
import { apiClient } from '@services/api';

// Formato compacto: D3/10  (D = disponíveis, 3 = livres, 10 = total)
const renderEventContent = (props) => {
  const available = Number(props.event._def.extendedProps.availables_cupos);
  const reserved  = Number(props.event._def.extendedProps.reserved_cupos);
  const free = Math.max(0, available - reserved);

  return (
    <div
      style={{
        width: '100%',
        cursor: 'pointer',
        padding: '2px 3px',
        lineHeight: 1.2,
        fontFamily: 'Poppins, Arial, sans-serif',
      }}
    >
      <div style={{ fontSize: '11px', fontWeight: 700, color: '#38bdf8', whiteSpace: 'nowrap' }}>
        D{free}/{available}
      </div>
      <div style={{ fontSize: '10px', color: '#ffffff', whiteSpace: 'nowrap' }}>
        R: {reserved}
      </div>
      <div style={{ fontSize: '12px', textAlign: 'center', color: '#ffffff' }}>🗑️</div>
    </div>
  );
};

const Calendar = (props) => {
  const [deleteModal, setDeleteModal] = useState(false);
  const [dateStart, setDateStart]     = useState(null);
  const [dateEnd, setDateEnd]         = useState(null);
  const [fromDate, setFromDate]       = useState(null);
  const [toDate, setToDate]           = useState(null);

  const calendarRef  = useRef(null);
  const containerRef = useRef(null);

  // ─── Monta events passando cupo_date como string pura (sem parse de timezone) ───
  const getEvents = () => {
    const cuposArray = Object.values(props.cupos);
    return cuposArray.map((obj) => ({
      id:   obj.id,
      date: obj.cupo_date,          // string "yyyy-MM-dd" — FullCalendar trata como all-day
      extendedProps: {
        cupo_date:       obj.cupo_date,
        availables_cupos: obj.availables_cupos,
        reserved_cupos:   obj.reserved_cupos,
        tour_id:          obj.tour_id,
      },
    }));
  };

  // ─── Resize ao montar ──────────────────────────────────────────────────────────
  useEffect(() => {
    const t1 = setTimeout(() => { calendarRef.current?.getApi().updateSize(); }, 150);
    const t2 = setTimeout(() => { calendarRef.current?.getApi().updateSize(); }, 500);

    let observer;
    if (containerRef.current && window.ResizeObserver) {
      observer = new ResizeObserver(() => { calendarRef.current?.getApi().updateSize(); });
      observer.observe(containerRef.current);
    }
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      observer?.disconnect();
    };
  }, []);

  // ─── Clique em evento existente → excluir esse dia ────────────────────────────
  const handleEventClick = (arg) => {
    // Usa a string original do banco para evitar problemas de timezone
    const raw = arg.event.extendedProps.cupo_date;        // "yyyy-MM-dd"
    const parsed = parseISO(raw);
    setDateStart(format(parsed, 'dd/MM/yyyy'));
    setDateEnd(null);
    setFromDate(raw);
    setToDate(raw);
    setDeleteModal(true);
  };

  // ─── Arrastar no calendário → excluir range (desktop) ────────────────────────
  const handleSelect = (arg) => {
    calendarRef.current?.getApi().unselect();

    arg.end.setDate(arg.end.getDate() - 1);               // FullCalendar end é exclusivo
    const from_date  = format(arg.start, 'yyyy-MM-dd');
    const to_date    = format(arg.end,   'yyyy-MM-dd');
    const dsStart    = format(arg.start, 'dd/MM/yyyy');
    const dsEnd      = format(arg.end,   'dd/MM/yyyy');

    setDateStart(dsStart);
    setFromDate(from_date);
    setToDate(to_date);
    setDateEnd(to_date !== from_date ? dsEnd : null);
    setDeleteModal(true);
  };

  // ─── Confirmar exclusão ────────────────────────────────────────────────────────
  const deleteCupos = (getSeats) => {
    const cuposArray = Object.values(props.cupos);
    const tour_id = cuposArray.find(c => c.tour_id)?.tour_id ?? props.tourId;

    apiClient.httpInstance
      .post('/seats/delete_from_period', { from_date: fromDate, to_date: toDate, tour_id })
      .then((response) => {
        if (response.status === 200 || response.status === 204) {
          setDeleteModal(false);
          getSeats();
        }
      });
  };

  return (
    <>
      <div ref={containerRef} style={{ width: '100%' }}>
        <FullCalendar
          ref={calendarRef}
          locale="pt-br"
          timeZone="local"
          events={getEvents()}
          eventColor="transparent"
          headerToolbar={{ start: 'prev', center: 'title', end: 'next' }}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          contentHeight="auto"
          selectable
          select={handleSelect}
          eventClick={handleEventClick}
          eventContent={renderEventContent}
        />
      </div>

      <DeleteModal
        open={deleteModal}
        close={() => setDeleteModal(false)}
        delete={() => deleteCupos(props.getSeats)}
        title="Excluir cupos?"
        description={`Excluir cupos do dia ${dateStart}${dateEnd ? ` até ${dateEnd}` : ''}? Essa ação é irreversível.`}
      />
    </>
  );
};

export default Calendar;
